// ============================================================================
// case-report-service.ts — Business logic + orchestration for Case Reports
// ============================================================================

import { App } from '@/services/app-compat';
import { api } from '@/services/api-class';
import { AccessAuthorities } from '@/models';
import { hasValue, hasStringValue, getOfficerLabel, getModuleType } from '@/utils/legacy-utils';
import moment from 'moment';

import { isInArray } from '@/utils/arrays';
import { getAvailableSubmodules } from '@/utils/auth';
import { ModuleType } from '@/constants/codes';
import { RelType } from '@/models/other-entities';
import { DATETIME_FORMAT_DISPLAY_LOCAL_MOMENT } from '@/utils/window-globals';
import { ReportClientGroup, ReportPerson, ReportVehicle, ReportBusiness } from '@/models/legacy/index';

// Lazy window-global lookups — resolved at call time, not module load time.
const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const getInsecureUUID = _wg('getInsecureUUID');
const hasReportApproval = _wg('hasReportApproval');
const stripImageSrcForSave = _wg('stripImageSrcForSave');
const selectModuleCallback = _wg('selectModuleCallback');

// ── CaseReport Controller Interface ─────────────────────────────────────

export interface CaseReportController {
  isSearchOpen(): boolean;
  activeTab: string;
  isSaving: boolean;
  narrativeContent: string;
  npsdConnectionsNew: any;
  narrativeEditor: any;
  openViewPersonModal(id: number): void;
  openViewVehicleModal(id: number): void;
  openViewBusinessModal(id: number): void;
  openViewClientModal(id: number): void;
  closeInvolvedSearchModal(): void;
  refreshNpsd(): void;
  destroy(): void;
}

// ── CaseRpt Global State ────────────────────────────────────────────────

class CaseRpt {
  static currentMDTRptController: CaseReportController | null = null;
  static currentMDTRpt: any;
  static newRpt = true;
  static updatingItem: any;
  static ofgItems: any;
  static vehicleOwners: any;
  static vehicles: any[] = [];
  static lockedReportId: any = null;
  static originalReportResult: any = null;
  static tempOriginalMDTReport: any = null;
  static gridRespondingAgencies: any = null;
  static ddlInvolving: any;
  static showAllPendingReports = false;
  static PendingReportResult: any[] = [];
  static caseReportAutoSaveInterval: any = null;
  static officerLabel: string | null = null;
  static officerPluralLabel: string | null = null;
}
(window as any).CaseRpt = CaseRpt;
export { CaseRpt };

// ── Officer/Agency Conversion ───────────────────────────────────────────

export function convertOfficersToFormFormat(data: any): any[] {
  const officers: any[] = [];

  if (data.primaryOfficer) {
    officers.push({
      uuid: getInsecureUUID(),
      id: null,
      officerId: data.primaryOfficer,
      name: '',
      isPrimary: true,
      dispatched: data.primaryDispatched,
      enroute: data.primaryEnroute,
      onScene: data.primaryOnScene,
      cleared: data.primaryCleared,
    });
  }

  if (data.backupUnits) {
    for (const backupUnit of data.backupUnits) {
      officers.push({
        uuid: getInsecureUUID(),
        id: backupUnit.id,
        officerId: backupUnit.officer,
        name: '',
        isPrimary: false,
        dispatched: backupUnit.dispatched,
        enroute: backupUnit.enroute,
        onScene: backupUnit.onScene,
        cleared: backupUnit.cleared,
      });
    }
  }

  return officers;
}

export function convertFormOfficersToReport(formOfficers: any[], report: any): void {
  const primary = formOfficers.find(o => o.isPrimary);
  const backups = formOfficers.filter(o => !o.isPrimary);

  if (primary) {
    report.primaryOfficer = primary.officerId;
    report.primaryDispatched = primary.dispatched;
    report.primaryEnroute = primary.enroute;
    report.primaryOnScene = primary.onScene;
    report.primaryCleared = primary.cleared;
  } else {
    report.primaryOfficer = null;
    report.primaryDispatched = null;
    report.primaryEnroute = null;
    report.primaryOnScene = null;
    report.primaryCleared = null;
  }

  report.backupUnits = backups.map(b => {
    const bu = new (window as any).BackUpUnit('');
    bu.id = b.id;
    bu.officer = b.officerId;
    bu.dispatched = b.dispatched;
    bu.enroute = b.enroute;
    bu.onScene = b.onScene;
    bu.cleared = b.cleared;
    return bu;
  });
}

export function convertAgenciesToFormFormat(agencies: any[]): any[] {
  if (!agencies) return [];

  return agencies.map(agency => ({
    uuid: getInsecureUUID(),
    id: agency.id,
    agency: agency.agency,
    people: agency.people,
    arrived: agency.arrived,
    notes: agency.notes,
  }));
}

export function convertFormAgenciesToReport(formAgencies: any[], report: any): void {
  report.respondingAgencies = formAgencies.map(va => {
    const ra = new (window as any).RespondingAgency('');
    ra.id = va.id;
    ra.agency = va.agency;
    ra.people = va.people;
    ra.arrived = va.arrived;
    ra.notes = va.notes;
    return ra;
  });
}

// ── Validation ──────────────────────────────────────────────────────────

export function validateReport(report: any): Array<{ message: string; type: string }> {
  const errors: Array<{ message: string; type: string }> = [];
  const natureOfCall = report.natureOfCall;
  if (!hasValue(natureOfCall) || natureOfCall.length === 0) {
    errors.push({ message: 'Please enter an incident nature.', type: 'error' });
  }
  if (!hasValue(report.incidentDescription) || report.incidentDescription === '') {
    errors.push({ message: 'Please enter an incident description.', type: 'error' });
  }
  return errors;
}

// ── Report Capability Check ─────────────────────────────────────────────

export function canModifyReport(report: any): boolean {
  if (!report) return false;
  if (report.isApproved || (report.isSubmitted && !hasReportApproval())) return false;
  return true;
}

// ── Prepare Report for Save ─────────────────────────────────────────────

export function prepareReportForSave(report: any, narrativeData: { content: string; attachmentInfoList: any[] }): void {
  report.narrative = narrativeData.content;
  report.narrativeAttachmentInfoList = narrativeData.attachmentInfoList;

  for (let i = 0; i < report.digitalEvidenceList.length; i++) {
    const evidence = report.digitalEvidenceList[i];
    if (evidence.attachmentInfoList?.length === 0 && evidence.attachmentList?.length > 0) {
      const ai = new (window as any).AttachmentInfo();
      ai.id = evidence.attachmentList[0].id;
      evidence.attachmentInfoList.push(ai);
    }
  }

  for (let i = 0; i < report.accident.accidentPersonList.length; i++) {
    const apl = report.accident.accidentPersonList[i];
    apl.isAtFault = parseInt(apl.isAtFault.toString());
    apl.isInjured = parseInt(apl.isInjured.toString());
    apl.isVehicleDamaged = parseInt(apl.isVehicleDamaged.toString());
  }
}

// ── Narrative Content for Save ──────────────────────────────────────────

export function getNarrativeContentForSave(
  narrativeEditorRef: any,
  narrativeContent: string,
): { content: string; attachmentInfoList: Array<{ id: number; activationKey: string | null }> } {
  // Try to get content from a React-exposed ref
  const narrativeEditor = narrativeEditorRef;
  if (narrativeEditor && typeof narrativeEditor.getContentForSave === 'function') {
    return narrativeEditor.getContentForSave();
  }

  // Fallback: process the content manually using stripImageSrcForSave
  const content = narrativeContent || '';
  const saveData = stripImageSrcForSave(content);

  const attachmentInfoList = saveData.attachmentInfoList.map((item: any) => ({
    id: item.id,
    activationKey: (window as any).editorAttachmentKeys?.[item.id] || null,
  }));

  return { content: saveData.content, attachmentInfoList };
}

// ── Backup Units from Call Card ─────────────────────────────────────────

export function getReportBackupUnitsFromCallCardBackupUnits(callCardBackupUnits: any[]): any[] {
  const reportBackupUnits: any[] = [];
  for (const callCardBackupUnit of callCardBackupUnits) {
    const reportBackupUnit = new (window as any).BackUpUnit('');
    let isPropFound = false;
    for (const backupUnitProp of Object.getOwnPropertyNames(reportBackupUnit)) {
      if (backupUnitProp !== 'id' && callCardBackupUnit[backupUnitProp] !== undefined) {
        (reportBackupUnit as any)[backupUnitProp] = callCardBackupUnit[backupUnitProp];
        isPropFound = true;
      }
    }
    if (isPropFound) {
      reportBackupUnits.push(reportBackupUnit);
    }
  }
  return reportBackupUnits;
}

// ── Build Report from Call Card Data ────────────────────────────────────

export function buildReportFromCallCard(report: any, callCardData: any): void {
  const incidentOccurredFrom = moment(callCardData.callDateTime).format(DATETIME_FORMAT_DISPLAY_LOCAL_MOMENT);
  report.callCardId = callCardData.id;
  report.natureOfCall = callCardData.natureOfCall;
  report.incidentOccurredFrom = incidentOccurredFrom;
  report.primaryOfficer = callCardData.primaryOfficer;
  report.primaryDispatched = callCardData.primaryDispatched;
  report.primaryEnroute = callCardData.primaryEnroute;
  report.primaryOnScene = callCardData.primaryOnScene;
  report.primaryCleared = callCardData.primaryCleared;
  report.backupUnits = getReportBackupUnitsFromCallCardBackupUnits(callCardData.backupUnits);
  report.callLocFormattedAddress = callCardData.callLocFormattedAddress;
  report.callLocAddress2 = callCardData.callLocAddress2;
  report.callLocName = callCardData.callLocName;
  report.callDisposition = callCardData.callDisposition;
  report.callDispositionValue = callCardData.callDispositionValue;
  report.dispositionNotes = callCardData.dispositionNotes;

  const natureOfCallValues = callCardData.natureOfCallValues ? callCardData.natureOfCallValues.join(', ') : '';
  const callLocation = report.callLocFormattedAddress;
  report.incidentDescription = `On ${incidentOccurredFrom}, a ${natureOfCallValues} occurred at ${callLocation}.`;

  if (hasValue(callCardData.clientIdAndSiteIds)) {
    const reportClientGroup = new ReportClientGroup();
    reportClientGroup.id = callCardData.clientIdAndSiteIds.clientId;
    reportClientGroup.sites = callCardData.clientIdAndSiteIds.siteIds;
    report.npsdConnectionsNew.clients = [reportClientGroup];
  }
  if (hasValue(callCardData.peopleIds)) {
    report.npsdConnectionsNew.people = callCardData.peopleIds.map((personId: any) => ({ id: personId }));
  }
  if (hasValue(callCardData.vehicleIds)) {
    report.npsdConnectionsNew.vehicles = callCardData.vehicleIds.map((vehicleId: any) => ({ id: vehicleId }));
  }
}

// ── Setup Involved (vehicle owners) ─────────────────────────────────────

export function setupInvolved(result: any, vehiclesArray: any[]): void {
  if (!result?.npsd?.vehicles) return;

  for (const vehicle of result.npsd.vehicles) {
    if (!vehicle) continue;

    const vo: any = {
      id: vehicle.id,
      Connections: { People: [], Businesses: [], Clients: [] },
    };

    for (const personOwner of vehicle.connections.personOwners) {
      vo.Connections.People.push(personOwner.id);
    }
    for (const businessOwner of vehicle.connections.businessOwners) {
      vo.Connections.Businesses.push(businessOwner.id);
    }
    for (const clientOwner of vehicle.connections.clientOwners) {
      vo.Connections.Clients.push(clientOwner.id);
    }

    vehiclesArray.push(vo);
  }
}

// ============================================================================
// Cleanup Functions (called from app.ts on navigation)
// ============================================================================

export function cleanUpCaseReport(): void {
  CaseRpt.currentMDTRpt = null;
  CaseRpt.currentMDTRptController = null;
  App.originalReportResult = null;
  // Clear search mode so search results don't show "Add to Report" buttons
  if (App.npsdSearchType === "addRpt" || App.npsdSearchType === "addviewRpt") {
    App.npsdSearchType = "";
  }
}
(window as any).cleanUpCaseReport = cleanUpCaseReport;

export function cleanUpCaseReportsListScreen(): void {
  if (CaseRpt) {
    CaseRpt.showAllPendingReports = false;
  }
}
(window as any).cleanUpCaseReportsListScreen = cleanUpCaseReportsListScreen;

// ============================================================================
// Lock/Unlock (called from app.ts on navigation)
// ============================================================================

function unlockLockedReport(): void {
  //If there's no module type, we might be switching modules, which would log out the user.
  if (!hasValue(CaseRpt.lockedReportId) || !hasStringValue(getModuleType())) {
    return;
  }
  api.lockReport(CaseRpt.lockedReportId, false, (lockResult: any) => {
    CaseRpt.lockedReportId = null;
  }, () => {
    CaseRpt.lockedReportId = null;
  });
}
(window as any).unlockLockedReport = unlockLockedReport;

// ============================================================================
// Report Capability Check
// ============================================================================

function canModifyCurrentReport(): boolean {
  return canModifyReport(CaseRpt.currentMDTRpt);
}
(window as any).canModifyCurrentReport = canModifyCurrentReport;

// ============================================================================
// Navigation Helpers
// ============================================================================

function goToCaseReportsScreen(): void {
  (window as any).OrnaReact.navigate.toCaseReportList();
}
(window as any).goToCaseReportsScreen = goToCaseReportsScreen;

function exitCaseReportScreenWithoutDirtyChecking(): void {
  App.originalReportResult = null; //ignore dirty checking when leaving screen
  goToCaseReportsScreen(); //go to case reports pending and history screen
}
(window as any).exitCaseReportScreenWithoutDirtyChecking = exitCaseReportScreenWithoutDirtyChecking;

function showCaseReportHelper(caseReportId: number | null = null): void {
  if (App.getModuleTypeFromURL() === ModuleType.mdt) {
    if (hasValue(caseReportId)) {
      (window as any).OrnaReact.navigate.toCaseReport(caseReportId, undefined, undefined, 'MDT');
    } else {
      (window as any).OrnaReact.navigate.toNewCaseReport('MDT');
    }
  } else if (App.getModuleTypeFromURL() === ModuleType.cad && isInArray(getAvailableSubmodules(), AccessAuthorities.ROLE_CASE_REPORT)) {
    if (hasValue(caseReportId)) {
      (window as any).OrnaReact.navigate.toCaseReport(caseReportId, undefined, undefined, 'CAD');
    } else {
      (window as any).OrnaReact.navigate.toNewCaseReport('CAD');
    }
  } else if (isInArray(getAvailableSubmodules(), AccessAuthorities.ROLE_MDT)) {
    api.selectModule("mdt", (result: any) => {
      selectModuleCallback(result);
      if (hasValue(caseReportId)) {
        (window as any).OrnaReact.navigate.toCaseReport(caseReportId, undefined, undefined, 'MDT');
      } else {
        (window as any).OrnaReact.navigate.toNewCaseReport('MDT');
      }
    }, () => { //error callback
    });
  } else {
    App.showPopupNotification('Permissions required to view Case Report.', 'error');
  }
}
(window as any).showCaseReportHelper = showCaseReportHelper;

function showNewCaseReport(): void {
  showCaseReportHelper();
}
(window as any).showNewCaseReport = showNewCaseReport;

function showCaseReport(caseReportId: number): void {
  showCaseReportHelper(caseReportId);
}
(window as any).showCaseReport = showCaseReport;

// ============================================================================
// NPSD Item Management (called from Search.ts, Utils.ts, React NPSD screens)
// ============================================================================

function NPSDItemToReport(type: string, id: any): void {
  // Check if there's an active case report
  if (!CaseRpt.currentMDTRpt) {
    App.showPopupNotification("No case report is currently open.", "warning");
    return;
  }

  let existing;

  if (type === "lnkRptAddPerson") {
    existing = CaseRpt.currentMDTRpt.npsdConnectionsNew.people.findIndex((e: any) => e.id.toString() === id.toString());
    if (existing >= 0) {
      App.showPopupNotification("Person already added to report.", "error");
      return;
    }
    // Fetch full person data then add
    api.getNPSDItemById(id, "person", (result: any) => {
      const p = new ReportPerson(result);
      // Ensure connections.roles exists
      if (!p.connections) {
        p.connections = { roles: [] };
      } else if (!p.connections.roles) {
        p.connections.roles = [];
      }
      CaseRpt.currentMDTRpt.npsdConnectionsNew.people.push(p);
      CaseRpt.currentMDTRptController?.refreshNpsd();
    });
  } else if (type === "lnkRptAddVehicle") {
    existing = CaseRpt.currentMDTRpt.npsdConnectionsNew.vehicles.findIndex((e: any) => e.id.toString() === id.toString());
    if (existing >= 0) {
      App.showPopupNotification("Vehicle already added to report.", "error");
      return;
    }
    // Fetch full vehicle data then add
    api.getNPSDItemById(id, "vehicle", (result: any) => {
      const v = new ReportVehicle(result);
      CaseRpt.currentMDTRpt.npsdConnectionsNew.vehicles.push(v);
      CaseRpt.currentMDTRptController?.refreshNpsd();
    });
  } else if (type === "lnkRptAddBusiness") {
    existing = CaseRpt.currentMDTRpt.npsdConnectionsNew.businesses.findIndex((e: any) => e.id.toString() === id.toString());
    if (existing >= 0) {
      App.showPopupNotification("Business already added to report.", "error");
      return;
    }
    // Fetch full business data then add
    api.getNPSDItemById(id, "business", (result: any) => {
      const b = new ReportBusiness(result);
      CaseRpt.currentMDTRpt.npsdConnectionsNew.businesses.push(b);
      CaseRpt.currentMDTRptController?.refreshNpsd();
    });
  } else if (type === "lnkRptAddClientGroup") {
    existing = CaseRpt.currentMDTRpt.npsdConnectionsNew.clients.findIndex((e: any) => e.id.toString() === id.toString());
    if (existing >= 0) {
      App.showPopupNotification("Client group already added to report.", "error");
      return;
    }
    // Fetch full client data then add
    api.getNPSDItemById(id, "client", (result: any) => {
      const cg = new ReportClientGroup(result);
      // The API result has 'sites' as an array of site objects
      // For ReportClientGroup: allSites = all available sites, sites = selected site IDs
      if (result.sites && Array.isArray(result.sites)) {
        cg.allSites = result.sites;
        cg.sites = []; // No sites selected initially
      } else {
        cg.allSites = [];
        cg.sites = [];
      }
      CaseRpt.currentMDTRpt.npsdConnectionsNew.clients.push(cg);
      CaseRpt.currentMDTRptController?.refreshNpsd();
    });
  }
}
(window as any).NPSDItemToReport = NPSDItemToReport;

function updateNPSDItemForReport(type: any, currentNpsdItem: any): void {
  if (!CaseRpt.currentMDTRpt) {
    return;
  }

  const id = currentNpsdItem.id;
  let connectionsNewItems;
  if (type === RelType.person) {
    connectionsNewItems = CaseRpt.currentMDTRpt.npsdConnectionsNew.people;
  } else if (type === RelType.vehicle) {
    connectionsNewItems = CaseRpt.currentMDTRpt.npsdConnectionsNew.vehicles;
  } else if (type === RelType.business) {
    connectionsNewItems = CaseRpt.currentMDTRpt.npsdConnectionsNew.businesses;
  } else if (type === RelType.client) {
    connectionsNewItems = CaseRpt.currentMDTRpt.npsdConnectionsNew.clients;
  }
  if (!connectionsNewItems) {
    App.showPopupNotification("Item type not found when updating item for report.", "error");
    return;
  }

  // Find and replace the item with updated data
  const idx = connectionsNewItems.findIndex((e: any) => e.id.toString() === id.toString());
  if (idx < 0) {
    // Item is not on the report - silently skip update (user may have viewed/edited an item not on the report)
    return;
  }

  // Preserve the connections/roles from the existing item if not in the update
  const existingItem = connectionsNewItems[idx];
  if (existingItem.connections && !currentNpsdItem.connections) {
    currentNpsdItem.connections = existingItem.connections;
  }
  if (existingItem.roles && !currentNpsdItem.roles) {
    currentNpsdItem.roles = existingItem.roles;
  }

  // Replace the item in the array
  connectionsNewItems.splice(idx, 1, currentNpsdItem);

  // Refresh React state
  CaseRpt.currentMDTRptController?.refreshNpsd();
}
(window as any).updateNPSDItemForReport = updateNPSDItemForReport;
